import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import {
  BseAnnouncementDto,
  BseDto,
  BseQuestionDto,
  BseQuestionStatus,
  NegotiationRoundDto,
  OrganizationInviteDto,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { displayDateWithTime } from "../../../../../common/formatting/displayDateWithTime";
import { useQueryParams } from "../../../../../common/useQueryParams";
import { NewQuestionModal } from "./NewQuestionModal";
import { QuestionsTable } from "./QuestionsTable";
import { SupplierQuestionModal } from "./SupplierQuestionModal";
interface Props {
  data: BseDto;
  canManageQuestions: boolean;
  canCreateNewQuestion?: boolean;
  newQuestionDisabledReason?: string;
  addComment: (values: {
    comment: string;
    question: BseQuestionDto;
    negotiationRoundId?: string;
    createAnnouncementDraft: boolean;
  }) => Promise<void>;
  changeStatus: (status: BseQuestionStatus, questionId: string, negotiationRoundId?: string) => Promise<boolean>;
  announcements: BseAnnouncementDto[];
  organizationInvites: OrganizationInviteDto[];
  createNewQuestion: (values: {
    subject: string;
    content: string;
    askedToOrganizationId: string;
    announcementId: string | null;
  }) => Promise<BseDto>;
}

export const SupplierQuestions = ({
  data,
  canManageQuestions,
  canCreateNewQuestion = true,
  newQuestionDisabledReason,
  addComment,
  changeStatus,
  announcements,
  organizationInvites,
  createNewQuestion,
}: Props) => {
  const [questionId, setQuestionId] = useState<string>();
  const newQuestionModal = useDisclosure();
  const params = useQueryParams();
  const questionIdParam = params.get("question");
  const { t } = useTranslation();

  const authState = useLoggedInWithOrgContextState();
  const supplierOptions = useMemo(() => {
    const names = new Set<string>();
    (data.questions ?? []).forEach((q: BseQuestionDto) => {
      if (q.askedToOrganization) names.add(q.askedToOrganization);
      if (q.askedByOrgName) names.add(q.askedByOrgName);
    });
    (data.negotiationContext?.rounds ?? []).forEach((r: NegotiationRoundDto) => {
      (r.questions ?? []).forEach((q: BseQuestionDto) => {
        if (q.askedToOrganization) names.add(q.askedToOrganization);
        if (q.askedByOrgName) names.add(q.askedByOrgName);
      });
    });
    const currentOrgName = authState.selectedOrg.name;
    if (currentOrgName) names.delete(currentOrgName);
    return Array.from(names);
  }, [data.questions, data.negotiationContext?.rounds, authState.selectedOrg.name]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | "__all__">("__all__");
  const filterBySelectedSupplier = (questions: BseQuestionDto[]): BseQuestionDto[] =>
    selectedSupplier === "__all__"
      ? questions
      : questions.filter(
          (q: BseQuestionDto) => q.askedToOrganization === selectedSupplier || q.askedByOrgName === selectedSupplier
        );
  const selectedQuestion: { question: BseQuestionDto; negotiationRoundId?: string } | undefined = useMemo(() => {
    const originalEventQuestion = (data.questions ?? []).find((q) => q.id === questionId);
    if (originalEventQuestion) return { question: originalEventQuestion, negotiationRoundId: undefined };
    const round = (data.negotiationContext?.rounds ?? []).find((r) => r.questions.find((q) => q.id === questionId));
    if (round) {
      return { question: round.questions.find((q) => q.id === questionId)!, negotiationRoundId: round.id };
    }
    return undefined;
  }, [questionId, data.questions, data.negotiationContext?.rounds]);

  const getInfoSentence = (q: BseQuestionDto): string => {
    const askedTo = q.askedToOrganization;
    const askedByOrg = q.askedByOrgName;
    const isMeAsker = q.askedByPerson.id === authState.me.id && q.askedByOrgName === authState.selectedOrg.name;
    if (isMeAsker) return `${t("Asked to")} ${askedTo} ${t("by you")}, ${displayDateWithTime(q.askedAt)}.`;
    return `${t("Asked by")} ${q.askedByPerson.firstName} ${t("in")} ${askedByOrg} ${t("to")} ${askedTo}, ${displayDateWithTime(q.askedAt)}.`;
  };

  useEffect(() => {
    if (questionIdParam) setQuestionId(questionIdParam);
  }, [questionIdParam]);

  const filteredEventQuestions = useMemo(
    () => filterBySelectedSupplier(data.questions ?? []),
    [data.questions, selectedSupplier]
  );

  return (
    <>
      <NewQuestionModal
        isOpen={newQuestionModal.isOpen}
        onClose={newQuestionModal.onClose}
        organizationInvites={organizationInvites}
        onCreate={async ({ subject, content, askedToOrganizationId }) =>
          createNewQuestion({ subject, content, askedToOrganizationId, announcementId: null })
        }
      />
      {selectedQuestion && (
        <SupplierQuestionModal
          addCommentDisabledReason={null}
          canManageQuestions={canManageQuestions}
          addComment={async (values: { comment: string; createAnnouncementDraft: boolean }) => {
            await addComment({
              comment: values.comment,
              question: selectedQuestion.question,
              negotiationRoundId: selectedQuestion.negotiationRoundId,
              createAnnouncementDraft: values.createAnnouncementDraft,
            });
            if (values.createAnnouncementDraft) {
              setQuestionId(undefined);
            }
          }}
          question={selectedQuestion.question}
          announcements={announcements}
          isOpen={true}
          close={() => {
            setQuestionId(undefined);
            params.clearSelectedQuestion();
          }}
          changeStatus={async (status: BseQuestionStatus) => {
            return changeStatus(status, selectedQuestion.question.id, selectedQuestion.negotiationRoundId);
          }}
        />
      )}
      <Flex width="full" pt="5" pb="8" align="center" gap={3}>
        <Tooltip
          isDisabled={canCreateNewQuestion}
          label={newQuestionDisabledReason || t("You can't ask questions while the negotiation round is a draft")}
        >
          <span>
            <Button
              leftIcon={<Icon as={FaPlus} w="15px" h="15px" />}
              colorScheme={"blue"}
              variant="outline"
              size={"sm"}
              onClick={newQuestionModal.onOpen}
              isDisabled={!canCreateNewQuestion}
            >
              {t("New dialog")}
            </Button>
          </span>
        </Tooltip>
        <Menu isLazy>
          <MenuButton as={Button} size="sm" variant="outline">
            {selectedSupplier === "__all__" ? t("All suppliers") : selectedSupplier}
          </MenuButton>
          <Portal>
            <MenuList zIndex={2000} maxW="300px">
              <MenuItem onClick={() => setSelectedSupplier("__all__")}>{t("All suppliers")}</MenuItem>
              {supplierOptions.map((org) => (
                <MenuItem key={org} onClick={() => setSelectedSupplier(org)}>
                  {org}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
      <Flex flex="1" minH="0" flexDirection="column">
        {(data.negotiationContext?.rounds?.length ?? 0) > 0 ? (
          <Accordion
            allowMultiple
            reduceMotion
            defaultIndex={[0, ...(data.negotiationContext?.rounds ?? []).map((_, idx) => idx + 1)]}
          >
            {(data.negotiationContext?.rounds ?? [])
              .slice()
              .sort((a, b) => b.roundNumber - a.roundNumber)
              .map((round) => {
                const filteredRoundQuestions = filterBySelectedSupplier(round.questions);
                const unresolvedCount = filteredRoundQuestions.filter(
                  (q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved
                ).length;
                const resolvedCount = filteredRoundQuestions.filter(
                  (q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved
                ).length;
                return (
                  <AccordionItem
                    key={round.id}
                    mb="2"
                    rounded="md"
                    bg="smBackground"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="smBorder"
                  >
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton
                            py="2.5"
                            px="3"
                            _hover={{ bg: "smBackgroundSecondary" }}
                            _expanded={{ bg: "smBackgroundSecondary" }}
                            roundedTop="md"
                          >
                            <Flex as="span" flex="1" textAlign="left" align="center" gap={2}>
                              <Text pt="1">
                                {t("Negotiation round")} {round.roundNumber}
                              </Text>
                              {!isExpanded ? (
                                <>
                                  {unresolvedCount > 0 ? (
                                    <Badge colorScheme="red" ml={2} rounded="md">
                                      {unresolvedCount} {t("Unresolved")}
                                    </Badge>
                                  ) : null}
                                  {resolvedCount > 0 ? (
                                    <Badge colorScheme="green" ml={1} rounded="md">
                                      {resolvedCount} {t("Resolved")}
                                    </Badge>
                                  ) : null}
                                </>
                              ) : null}
                            </Flex>
                            <AccordionIcon color="smMuted" />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4} px="0" roundedBottom="md">
                          <QuestionsTable
                            items={filteredRoundQuestions}
                            getInfoSentence={getInfoSentence}
                            onRowClick={(id: string) => {
                              setQuestionId(id);
                              params.setSelectedQuestion(id);
                            }}
                            emptyText={t("No questions have been asked")}
                          />
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                );
              })}
            <AccordionItem
              key={data.id}
              mb="2"
              rounded="md"
              bg="smBackground"
              boxShadow="sm"
              border="1px solid"
              borderColor="smBorder"
            >
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton
                      py="2.5"
                      px="3"
                      _hover={{ bg: "smBackgroundSecondary" }}
                      _expanded={{ bg: "smBackgroundSecondary" }}
                      roundedTop="md"
                    >
                      <Flex as="span" flex="1" textAlign="left" align="center" gap={2}>
                        <Text pt="1">{data.title}</Text>
                        {!isExpanded ? (
                          <>
                            {filteredEventQuestions.filter(
                              (q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved
                            ).length > 0 ? (
                              <Badge colorScheme="red" ml={2} rounded="md">
                                {
                                  filteredEventQuestions.filter(
                                    (q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved
                                  ).length
                                }{" "}
                                {t("Unresolved")}
                              </Badge>
                            ) : null}
                            {filteredEventQuestions.filter(
                              (q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved
                            ).length > 0 ? (
                              <Badge colorScheme="green" ml={1} rounded="md">
                                {
                                  filteredEventQuestions.filter(
                                    (q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved
                                  ).length
                                }{" "}
                                {t("Resolved")}
                              </Badge>
                            ) : null}
                          </>
                        ) : null}
                      </Flex>
                      <AccordionIcon color="smMuted" />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} px="0" roundedBottom="md">
                    <QuestionsTable
                      items={filteredEventQuestions}
                      getInfoSentence={getInfoSentence}
                      onRowClick={(id: string) => {
                        setQuestionId(id);
                        params.setSelectedQuestion(id);
                      }}
                      emptyText={t("No questions have been asked")}
                    />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>
        ) : (
          <QuestionsTable
            items={filteredEventQuestions}
            bordered={true}
            getInfoSentence={getInfoSentence}
            onRowClick={(id: string) => {
              setQuestionId(id);
              params.setSelectedQuestion(id);
            }}
            emptyText={t("No questions have been asked")}
          />
        )}
      </Flex>
    </>
  );
};

export const sortByQuestionStatus = (
  a: {
    status: BseQuestionStatus;
  },
  b: {
    status: BseQuestionStatus;
  }
): number => {
  const statusOrder = [BseQuestionStatus.Unresolved, BseQuestionStatus.Resolved];
  return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
};
